/**
 * AxDataGrid 공통 검색(푸터 좌측) — 클라이언트 행을 "화면에 보이는 글자" 기준으로 거른다.
 *
 * AG Grid quickFilterText 대신 직접 거르는 이유:
 * - 그룹 헤더 pseudo-row(full-width)가 검색에 걸려 사라지지 않게, 그룹핑 **전** 행에 적용한다.
 * - 상태 코드처럼 라벨로 바꿔 보이는 컬럼(valueFormatter·StatusTagRenderer labels)을 라벨로도 찾는다.
 * 권한으로 숨겨진 컬럼은 호출부가 빼고 넘긴다(검색으로 숨긴 값을 추측하지 못하게).
 */
import { reactive } from 'vue';
import type { AxColumnDef } from './types';

export interface AxDataGridDefaults {
  /** 클라이언트 모드 그리드 푸터 좌측 공통 검색 기본값 (그리드별 `quickSearch` prop 이 우선). */
  quickSearch: boolean;
}

/** 앱 단위 AxDataGrid 기본값 — 부트스트랩에서 `setAxDataGridDefaults` 로 바꾼다. */
export const axDataGridDefaults = reactive<AxDataGridDefaults>({ quickSearch: false });

export function setAxDataGridDefaults(patch: Partial<AxDataGridDefaults>): void {
  Object.assign(axDataGridDefaults, patch);
}

function readPath(row: unknown, field: string): unknown {
  let cur: unknown = row;
  for (const key of field.split('.')) {
    if (cur === null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[key];
  }
  return cur;
}

function pushText(out: string[], value: unknown): void {
  if (value === null || value === undefined || value === '') return;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    out.push(String(value));
  } else if (value instanceof Date) {
    out.push(value.toISOString());
  }
}

/** 한 행의 검색 대상 문자열 — 원값 + 포맷값 + 라벨 맵 값을 이어 붙인다(소문자). */
export function quickSearchRowText<TRow>(row: TRow, columns: readonly AxColumnDef<TRow>[]): string {
  const out: string[] = [];
  for (const col of columns) {
    let value: unknown =
      typeof col.field === 'string' && col.field !== '' ? readPath(row, col.field) : undefined;
    pushText(out, value);
    // valueGetter 가 라벨로 바꿔 보여주는 컬럼(예: 품목유형 코드→이름)도 화면 글자로 찾힌다
    if (typeof col.valueGetter === 'function') {
      try {
        const got = (col.valueGetter as (p: unknown) => unknown)({ data: row, colDef: col });
        pushText(out, got);
        if (value === undefined) value = got;
      } catch {
        // grid api 를 기대하는 getter 는 건너뛴다
      }
    }
    if (typeof col.valueFormatter === 'function') {
      try {
        pushText(
          out,
          (col.valueFormatter as (p: unknown) => unknown)({ value, data: row, colDef: col }),
        );
      } catch {
        // 포맷터가 grid api 등을 기대하면 원값만 쓴다
      }
    }
    const params = col.cellRendererParams as { labels?: unknown } | undefined;
    if (
      params &&
      typeof params === 'object' &&
      params.labels &&
      typeof params.labels === 'object'
    ) {
      pushText(out, (params.labels as Record<string, unknown>)[String(value)]);
    }
  }
  return out.join('\u0001').toLowerCase();
}

/**
 * 검색어 → 행 판정 함수. 공백으로 나눈 낱말이 **모두** 포함돼야 통과(AND).
 * 검색어가 비면 null(거르지 않음).
 */
export function buildQuickSearchMatcher<TRow>(
  columns: readonly AxColumnDef<TRow>[],
  text: string,
): ((row: TRow) => boolean) | null {
  const tokens = text.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return null;
  const cache = new WeakMap<object, string>();
  return (row: TRow) => {
    let hay: string | undefined;
    if (row !== null && typeof row === 'object') {
      hay = cache.get(row);
      if (hay === undefined) {
        hay = quickSearchRowText(row, columns);
        cache.set(row, hay);
      }
    } else {
      hay = quickSearchRowText(row, columns);
    }
    return tokens.every((t) => hay.includes(t));
  };
}
